import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listStaffRoles,
  createStaffRole,
  updateStaffRole,
  deleteStaffRole,
  type StaffRole,
} from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, Plus, Trash2, AlertTriangle } from 'lucide-react';

export default function StaffRolesPage() {
  const queryClient = useQueryClient();

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['staff-roles'],
    queryFn: () => listStaffRoles(),
  });

  const [editing, setEditing] = useState<StaffRole | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<StaffRole | null>(null);
  const [name, setName] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['staff-roles'] });

  const createMutation = useMutation({
    mutationFn: (roleName: string) => createStaffRole({ name: roleName }),
    onSuccess: () => {
      invalidate();
      setCreating(false);
      setName('');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, roleName }: { id: string; roleName: string }) =>
      updateStaffRole(id, { name: roleName }),
    onSuccess: () => {
      invalidate();
      setEditing(null);
      setName('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteStaffRole(id),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
    },
  });

  const openCreate = () => {
    setName('');
    setCreating(true);
  };

  const openEdit = (role: StaffRole) => {
    setName(role.name);
    setEditing(role);
  };

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    if (editing) updateMutation.mutate({ id: editing.id, roleName: trimmed });
    else createMutation.mutate(trimmed);
  };

  const formError = (createMutation.error ?? updateMutation.error) as Error | null;
  const dialogOpen = creating || editing !== null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Staff Roles</h1>
          <p className="text-sm text-muted-foreground">
            The roles staff can be qualified for and assigned to (e.g. stage manager, artist care,
            sound engineer).
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New role
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Roles</CardTitle>
          <CardDescription>Used across availability, requirements and shift assignment.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : roles.length === 0 ? (
            <p className="text-sm text-muted-foreground">No roles yet. Create your first one.</p>
          ) : (
            <ul className="divide-y">
              {roles.map((role) => (
                <li key={role.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium">{role.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{role.slug}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(role)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDeleting(role)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCreating(false);
            setEditing(null);
            setName('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit role' : 'New role'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Rename this role.' : 'Add a role staff can be assigned to.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm}>
            <div className="space-y-2 px-4 sm:px-6 py-4">
              <label className="text-sm font-medium">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Stage manager"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                autoFocus
                required
              />
              {formError && <p className="text-sm text-destructive">{formError.message}</p>}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreating(false);
                  setEditing(null);
                  setName('');
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {createMutation.isPending || updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete {deleting?.name}?
            </DialogTitle>
            <DialogDescription>
              This removes the role from all staff qualifications and requirements. This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleting && deleteMutation.mutate(deleting.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
